import React, { useState } from 'react';
import { Mail, Phone, Send, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';

export const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    reason: '',
    message: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const resp = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(data?.error || 'Failed to send message');
      }

      toast({
        title: "Message Sent Successfully!",
        description: "We'll get back to you within 24 hours.",
      });

      setFormData({
        fullName: '',
        email: '',
        company: '',
        phone: '',
        reason: '',
        message: ''
      });
    } catch (err) {
      toast({
        title: "Message not sent",
        description: err?.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 md:w-96 h-64 md:h-96 bg-[#5B42FF]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 md:w-96 h-64 md:h-96 bg-[#5B42FF]/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-xs md:text-sm font-semibold text-[#5B42FF] uppercase tracking-wider mb-3 md:mb-4">Contact Us</h2>
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
            Let's Drive Impact Together
          </h3>
          <p className="text-base md:text-xl text-white/70 max-w-3xl mx-auto px-4">
            Connect with us to explore how advanced digital solutions can support your next initiative.
          </p>
          <div className="w-20 md:w-24 h-1 bg-[#5B42FF] mx-auto mt-4 md:mt-6"></div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 md:gap-12 mt-12 md:mt-16">
          <div className="lg:col-span-2">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl md:rounded-2xl p-6 md:p-8">
              <h4 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6 flex items-center">
                <Send className="w-5 h-5 md:w-6 md:h-6 text-[#5B42FF] mr-3" />
                Send Us a Message
              </h4>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="fullName" className="text-white/90 mb-2 block">
                    Full Name <span className="text-[#5B42FF]">*</span>
                  </Label>
                  <Input
                    id="fullName"
                    placeholder="First and last name"
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    required
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#5B42FF] focus:ring-[#5B42FF]"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-white/90 mb-2 block">
                    Email Address <span className="text-[#5B42FF]">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#5B42FF] focus:ring-[#5B42FF]"
                  />
                </div>

                <div>
                  <Label htmlFor="company" className="text-white/90 mb-2 block">
                    Company / Organization Name
                  </Label>
                  <Input
                    id="company"
                    placeholder="Your organization"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#5B42FF] focus:ring-[#5B42FF]"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-white/90 mb-2 block">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+966 XX XXX XXXX"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#5B42FF] focus:ring-[#5B42FF]"
                  />
                </div>

                <div>
                  <Label htmlFor="reason" className="text-white/90 mb-2 block">
                    Reason for Inquiry <span className="text-[#5B42FF]">*</span>
                  </Label>
                  <Select value={formData.reason} onValueChange={(value) => handleChange('reason', value)} required>
                    <SelectTrigger className="bg-white/5 border-white/20 text-white focus:border-[#5B42FF] focus:ring-[#5B42FF]">
                      <SelectValue placeholder="Select one" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/20">
                      <SelectItem value="general">General Inquiry</SelectItem>
                      <SelectItem value="products">Products & Services</SelectItem>
                      <SelectItem value="partnership">Partnership Opportunity</SelectItem>
                      <SelectItem value="government">Government Collaboration</SelectItem>
                      <SelectItem value="support">Support</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-white/90 mb-2 block">
                    Message <span className="text-[#5B42FF]">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Tell us about your needs, project, or inquiry"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    required
                    rows={5}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-[#5B42FF] focus:ring-[#5B42FF] resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-[#5B42FF] hover:bg-[#4a35db] text-white transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-[#5B42FF]/50 group disabled:opacity-70 disabled:hover:scale-100"
                >
                  <Send className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform duration-300" />
                  {isSubmitting ? "Sending..." : "Submit Message"}
                </Button>

                <p className="text-sm text-white/50 text-center">
                  By submitting this form, you agree to be contacted by our team.
                </p>
              </form>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl md:rounded-2xl p-6 md:p-8">
              <h4 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6">Contact Us Directly</h4>
              <p className="text-sm md:text-base text-white/70 mb-4 md:mb-6">
                Prefer to reach us directly? You can contact us through the following channels:
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-3 md:space-x-4 group">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#5B42FF]/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#5B42FF]/30 transition-colors duration-300">
                    <Mail className="w-5 h-5 md:w-6 md:h-6 text-[#5B42FF]" />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs md:text-sm mb-1">Email</p>
                    <a href="mailto:info@revotech.sa" className="text-sm md:text-base text-white hover:text-[#5B42FF] transition-colors duration-300 break-all">
                      info@revotech.sa
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-3 md:space-x-4 group">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#5B42FF]/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#5B42FF]/30 transition-colors duration-300">
                    <Phone className="w-5 h-5 md:w-6 md:h-6 text-[#5B42FF]" />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs md:text-sm mb-1">Phone</p>
                    <a href="tel:+966554855969" className="text-sm md:text-base text-white hover:text-[#5B42FF] transition-colors duration-300">
                      +966 55 48 55 969
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#5B42FF]/10 to-transparent backdrop-blur-sm border border-[#5B42FF]/30 rounded-xl md:rounded-2xl p-6 md:p-8">
              <CheckCircle2 className="w-8 h-8 md:w-10 md:h-10 text-[#5B42FF] mb-3 md:mb-4" />
              <h5 className="text-base md:text-lg font-semibold text-white mb-2">Quick Response</h5>
              <p className="text-white/70 text-xs md:text-sm">
                Our team typically responds within 24 hours during business days.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
